import React, { Fragment } from 'react';

import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import { styled, useTheme } from '@mui/material';

import { useGetEffectiveClientScopes } from '../hooks/scopes';
import { useGetSideMenuSections } from './hooks/sideMenuSections';
import SideMenuItem from './ui/SideMenuItem';
import WarningBanner from './ui/WarningBanner';

const StyComponentWrapper = styled('div')(({ theme }) => ({
    backgroundColor: theme.palette.lightGrey,
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    overflowY: 'auto',
    padding: theme.spacing(5),
}));

const StyMenuContainer = styled('div')(({ theme }) => ({
    flexShrink: 0,
    overflowY: 'auto',
    padding: theme.spacing(2),
    width: '320px',
}));

type EditUserModalContentProps = {
    activeSectionTitle: string;
    otherUsersEmails: string[];
    setActiveSectionTitle: (arg: string) => void;
    userId: number;
};

const EditUserModalContent = ({
    activeSectionTitle,
    otherUsersEmails,
    setActiveSectionTitle,
    userId,
}: EditUserModalContentProps): JSX.Element => {
    const theme = useTheme();

    const { scopes } = useGetEffectiveClientScopes();

    const sideMenuSections = useGetSideMenuSections(
        otherUsersEmails,
        scopes,
        userId,
    );
    const groupedSideMenuSectionsChildren = sideMenuSections
        .map((section) => section.children)
        .flat();

    const activeSection = groupedSideMenuSectionsChildren.find(
        (child) => activeSectionTitle === child.title,
    );

    return (
        <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
            <StyMenuContainer>
                {sideMenuSections.map((section) => (
                    <Fragment key={section.title}>
                        <Typography
                            sx={{ margin: `${theme.spacing(2)} 0` }}
                            variant="h5"
                        >
                            {section.title}
                        </Typography>
                        <List disablePadding>
                            {section.children.map((child) => (
                                <SideMenuItem
                                    key={child.title}
                                    activeSectionTitle={activeSectionTitle}
                                    item={child}
                                    setActiveSectionTitle={
                                        setActiveSectionTitle
                                    }
                                />
                            ))}
                        </List>
                    </Fragment>
                ))}
            </StyMenuContainer>
            {activeSection && (
                <StyComponentWrapper>
                    {!!activeSection?.warningMessage && (
                        <WarningBanner message={activeSection.warningMessage} />
                    )}
                    {activeSection?.component}
                </StyComponentWrapper>
            )}
        </div>
    );
};

export default EditUserModalContent;
